import { THead, TableShell, Td, Th, Tr, cn } from '@ambernord/ui';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../lib/format.js';

/**
 * Admina izmaksu sadalījuma tabula (§4.9 — TIKAI platformas skatam!). Pieņem gan DB
 * komponentes (string summas no GET /:id), gan `breakdown` komponentes (skaitļi no
 * /combinations un /draft). Klientam šo NEKAD nerenderē — tam ir kategoriju kopsavilkums.
 */
export interface BreakdownRowInput {
  kind: string;
  label?: string | null;
  amount: number | string;
  pct?: number | string | null;
  rationale?: string | null;
}

export function BreakdownTable({
  components,
  currency,
  subtotal,
  total,
}: {
  components: BreakdownRowInput[];
  currency: string;
  subtotal?: number | string | null;
  total?: number | string | null;
}) {
  const { t, i18n } = useTranslation('offers');
  const locale = i18n.language;

  return (
    <TableShell>
      <THead>
        <tr>
          <Th>{t('columns.component')}</Th>
          <Th className="text-right">{t('columns.amount')}</Th>
          <Th className="text-right">{t('columns.pct')}</Th>
          <Th>{t('columns.rationale')}</Th>
        </tr>
      </THead>
      <tbody>
        {components.map((c, i) => (
          <Tr key={`${c.kind}-${String(i)}`}>
            <Td
              className={cn(
                'font-medium',
                c.kind === 'margin' && 'text-amber-700 dark:text-amber-400',
              )}
            >
              {t(`costKinds.${c.kind}`, { defaultValue: c.kind })}
              {c.label ? (
                <span className="ml-1 text-xs text-slate-400 dark:text-slate-500">({c.label})</span>
              ) : null}
            </Td>
            <Td className="text-right tabular-nums">
              {formatNumber(c.amount, locale)} {currency}
            </Td>
            <Td className="text-right tabular-nums">
              {c.pct != null && c.pct !== '' ? `${formatNumber(c.pct, locale)}%` : '—'}
            </Td>
            <Td className="text-xs text-slate-500 dark:text-slate-400">{c.rationale ?? '—'}</Td>
          </Tr>
        ))}
        {subtotal != null || total != null ? (
          <>
            {subtotal != null ? (
              <Tr>
                <Td className="font-semibold">{t('labels.subtotal')}</Td>
                <Td className="text-right font-semibold tabular-nums">
                  {formatNumber(subtotal, locale)} {currency}
                </Td>
                <Td />
                <Td />
              </Tr>
            ) : null}
            {total != null ? (
              <Tr className="bg-slate-50 dark:bg-slate-950/40">
                <Td className="font-bold text-slate-900 dark:text-white">{t('labels.total')}</Td>
                <Td className="text-right text-base font-bold tabular-nums text-slate-900 dark:text-white">
                  {formatNumber(total, locale)} {currency}
                </Td>
                <Td />
                <Td />
              </Tr>
            ) : null}
          </>
        ) : null}
      </tbody>
    </TableShell>
  );
}
